import { api } from "./api";

/**
 * 미디어 그룹 API (/api/media-groups).
 * 기존 슬라이드 전용 그룹(slide.groupId 문자열 + /post/{id}/slide-groups)을 대체한다.
 * 구 API(getSlideGroupsByPost / updateSlideGroup)는 DB 마이그레이션 시 제거 예정.
 */

export type MediaType = "SLIDE" | "PHOTO" | "GROSS";

export interface MediaGroup {
  mediaGroupId: number;
  postId: number;
  mediaType: MediaType;
  name: string;
}

/**
 * 게시글의 미디어 그룹 목록. mediaType 을 생략하면 전체 종류를 반환한다.
 * 주의: 그룹별 미디어 개수는 내려주지 않으므로, 개수/소속은 미디어 목록에서 계산해야 한다.
 */
export const getMediaGroups = async (
  postId: number,
  mediaType?: MediaType
): Promise<MediaGroup[]> => {
  try {
    const params: Record<string, any> = { postId };
    if (mediaType) params.mediaType = mediaType;
    const { data } = await api().get("/media-groups", { params });
    return Array.isArray(data?.data) ? data.data : [];
  } catch (e) {
    console.error("media group list error:", e);
    return [];
  }
};

/** 그룹 생성. mediaIds 를 함께 주면 해당 미디어를 바로 배정한다. */
export const createMediaGroup = async (payload: {
  postId: number;
  mediaType: MediaType;
  name: string;
  mediaIds?: number[];
}): Promise<MediaGroup> => {
  try {
    const { data } = await api().post("/media-groups", {
      postId: payload.postId,
      mediaType: payload.mediaType,
      name: payload.name,
      mediaIds: payload.mediaIds ?? [],
    });
    return data.data;
  } catch (e) {
    console.error("media group create error:", e);
    throw new Error("그룹 생성에 실패했습니다.");
  }
};

/** 그룹 정보 수정. 현재 변경 가능한 필드는 이름뿐이다. */
export const renameMediaGroup = async (
  mediaGroupId: number,
  name: string
): Promise<MediaGroup> => {
  try {
    const { data } = await api().patch(`/media-groups/${mediaGroupId}`, { name });
    return data.data;
  } catch (e) {
    console.error("media group rename error:", e);
    throw new Error("그룹 이름 수정에 실패했습니다.");
  }
};

/** 그룹 삭제. 속해 있던 미디어는 삭제되지 않고 '그룹없음' 상태가 된다. */
export const deleteMediaGroup = async (mediaGroupId: number): Promise<void> => {
  try {
    await api().delete(`/media-groups/${mediaGroupId}`);
  } catch (e) {
    console.error("media group delete error:", e);
    throw new Error("그룹 삭제에 실패했습니다.");
  }
};

/**
 * 그룹의 포함 미디어를 mediaIds 로 **전체 교체**한다.
 * - 목록에서 빠진 미디어는 그룹에서 제거되고, 빈 배열을 보내면 그룹이 비워진다.
 * - 다른 그룹에 속해 있던 미디어를 포함하면 그쪽에서 그대로 옮겨온다.
 *
 * ※ 교체 방식이라 "현재 멤버 전체"를 정확히 알고 보내야 한다. 보내지 않은 멤버는 조용히 빠진다.
 *   사용자가 전체 목록을 보고 편집한 경우(그룹 편집 모달)에만 쓰고,
 *   일부만 건드리는 경우에는 단건 API(assignMediaToGroup / removeMediaFromGroup)를 쓸 것.
 */
export const setMediaGroupItems = async (
  mediaGroupId: number,
  mediaIds: number[]
): Promise<MediaGroup> => {
  try {
    const { data } = await api().put(`/media-groups/${mediaGroupId}/items`, { mediaIds });
    return data.data;
  } catch (e) {
    console.error("media group items update error:", e);
    throw new Error("그룹 미디어 변경에 실패했습니다.");
  }
};

/**
 * 미디어 하나를 이 그룹으로 배정한다. 다른 그룹에 속해 있었다면 그대로 이동한다.
 * 그 미디어 한 건만 건드리므로 다른 멤버를 알 필요가 없다 → 교체 API 보다 안전하다.
 */
export const assignMediaToGroup = async (
  mediaGroupId: number,
  mediaId: number
): Promise<MediaGroup> => {
  try {
    const { data } = await api().put(`/media-groups/${mediaGroupId}/items/${mediaId}`);
    return data.data;
  } catch (e) {
    console.error("media group item assign error:", e);
    throw new Error("그룹 배정에 실패했습니다.");
  }
};

/** 미디어 하나를 이 그룹에서 제외한다 (그룹없음 상태가 된다). */
export const removeMediaFromGroup = async (
  mediaGroupId: number,
  mediaId: number
): Promise<void> => {
  try {
    await api().delete(`/media-groups/${mediaGroupId}/items/${mediaId}`);
  } catch (e) {
    console.error("media group item remove error:", e);
    throw new Error("그룹 해제에 실패했습니다.");
  }
};

/**
 * 미디어 여러 개를 한 그룹으로 옮긴다(그룹 해제는 targetGroupId = null).
 *
 * 상황별로 가장 안전한 API 를 고른다.
 * - 그룹 해제: 미디어별 단건 제외. 다른 그룹의 멤버십은 건드리지 않는다.
 * - 1건 배정: 단건 배정. 대상 그룹의 기존 멤버를 몰라도 되므로 교체로 인한 유실 위험이 없다.
 * - 여러 건 배정: 대상 그룹의 "기존 멤버 + 옮길 미디어"를 한 번에 PUT.
 *   요청이 1회라 중간에 끊겨 일부만 반영되는 일이 없다.
 *
 * @param membership 현재 소속 맵 (mediaId → mediaGroupId | null). 화면이 들고 있는 미디어 목록에서 만든다.
 */
export const moveMediaToGroup = async (
  mediaIds: number[],
  targetGroupId: number | null,
  membership: Map<number, number | null>
): Promise<void> => {
  if (mediaIds.length === 0) return;

  // 그룹 해제
  if (targetGroupId == null) {
    for (const mediaId of mediaIds) {
      const gid = membership.get(mediaId);
      if (gid != null) await removeMediaFromGroup(gid, mediaId);
    }
    return;
  }

  // 이미 대상 그룹에 있는 건 건너뛴다
  const moving = mediaIds.filter((id) => membership.get(id) !== targetGroupId);
  if (moving.length === 0) return;

  if (moving.length === 1) {
    await assignMediaToGroup(targetGroupId, moving[0]);
    return;
  }

  const next = new Set<number>(moving);
  membership.forEach((gid, mediaId) => {
    if (gid === targetGroupId) next.add(mediaId);
  });
  await setMediaGroupItems(targetGroupId, [...next]);
};
